import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { PrimeReactProvider } from "primereact/api";

import { ColorThemeName } from "../../design-tokens";
import { mergeThemeVariables } from "./variables";

import "./prime-theme.css";

type PrimeReactProviderValue = NonNullable<React.ComponentProps<typeof PrimeReactProvider>["value"]>;

export type ThemeProviderProps = PropsWithChildren<{
  defaultTheme?: ColorThemeName;
  target?: HTMLElement | null;
  primeReactConfig?: Partial<PrimeReactProviderValue>;
}>;

interface ThemeContextType {
  theme: ColorThemeName;
  setTheme: (theme: ColorThemeName) => void;
  toggleTheme: () => void;
}

const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

const applyVariables = (element: HTMLElement, variables: Record<string, string>): void => {
  Object.entries(variables).forEach(([name, value]) => {
    element.style.setProperty(name, value);
  });
};

const clearVariables = (element: HTMLElement, variables: Record<string, string>): void => {
  Object.keys(variables).forEach((name) => {
    element.style.removeProperty(name);
  });
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  defaultTheme,
  target,
  primeReactConfig,
  children
}) => {
  const [theme, setThemeState] = React.useState<ColorThemeName>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("app-theme") as ColorThemeName;
      if (stored === "light" || stored === "dark") return stored;
    }
    return defaultTheme ?? "light";
  });

  const setTheme = (newTheme: ColorThemeName): void => {
    setThemeState(newTheme);
    localStorage.setItem("app-theme", newTheme);
  };

  const toggleTheme = (): void => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  useEffect(() => {
    const element = target ?? document.documentElement;
    const variables = mergeThemeVariables(theme);

    applyVariables(element, variables);
    element.dataset.theme = theme;

    return () => {
      clearVariables(element, variables);
      if (element.dataset.theme === theme) {
        delete element.dataset.theme;
      }
    };
  }, [theme, target]);

  const providerValue = useMemo<PrimeReactProviderValue>(() => ({
    ripple: true,
    unstyled: false,
    pt: {},
    ...(primeReactConfig ?? {})
  }), [primeReactConfig]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      <PrimeReactProvider value={providerValue}>{children}</PrimeReactProvider>
    </ThemeContext.Provider>
  );
};
